import Icon from "@components/Atoms/Icon";
import type ApiUrlCreator from "@core-ui/ApiServices/ApiUrlCreator";
import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import ModalToOpenService from "@core-ui/ContextServices/ModalToOpenService/ModalToOpenService";
import ModalToOpen from "@core-ui/ContextServices/ModalToOpenService/model/ModalsToOpen";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import WorkspaceService from "@core-ui/ContextServices/Workspace";
import { getEditorStore } from "@core-ui/stores/EditorStore";
import EnterpriseApi from "@ext/enterprise/EnterpriseApi";
import t from "@ext/localization/locale/translate";
import astToParagraphs from "@ext/StyleGuide/logic/astToParagraphs";
import { getSuggestionItems } from "@ext/StyleGuide/logic/getSuggestionItems";
import type { CheckSuggestion } from "@ics/gx-vector-search";
import { DropdownMenuItem } from "@ui-kit/Dropdown";
import { toast } from "@ui-kit/Toast";

export const checkArticleWithStyleGuide = async (gesUrl: string, apiUrlCreator: ApiUrlCreator) => {
	const editor = getEditorStore().editor;
	if (!editor || editor.isDestroyed) return;
	const doc = editor.state.doc;
	ModalToOpenService.setValue(ModalToOpen.Loading, { title: t("enterprise.admin.check.checking-article") });

	try {
		const res = await FetchService.fetch(apiUrlCreator.getArticleEditorContent());
		if (!res.ok) return;
		const paragraphs = astToParagraphs(await res.json());

		const result = await new EnterpriseApi(gesUrl).checkStyleGuide(paragraphs);
		if (!result) return;

		const isError = "code" in result && result.code !== 200;
		if (isError) return toast(result.message, { status: "error", icon: "alert-circle", focus: "medium" });

		// The check takes minutes: meanwhile the editor may be rebuilt or destroyed, or its text edited.
		const isStale = editor.isDestroyed || getEditorStore().editor !== editor || editor.state.doc !== doc;
		if (isStale) return toast(t("style-guide.article-changed"), { status: "warning", focus: "medium" });

		const suggestionItems = getSuggestionItems(result as CheckSuggestion[], paragraphs);
		editor.commands.markSuggestions(suggestionItems);
	} finally {
		ModalToOpenService.resetValue();
	}
};

const EnterpriseCheckStyleGuide = () => {
	const workspace = WorkspaceService.current();

	const gesUrl = PageDataContextService.value.conf.enterprise.gesUrl;
	const apiUrlCreator = ApiUrlCreatorService.value;

	if (!workspace?.enterprise?.modules?.styleGuide) return null;

	const checkArticle = async () => {
		if (!workspace?.enterprise?.modules?.styleGuide) return;
		await checkArticleWithStyleGuide(gesUrl, apiUrlCreator);
	};

	return (
		<DropdownMenuItem onSelect={checkArticle}>
			<Icon code="spell-check" />
			{t("style-guide.check-with-style-guide")}
		</DropdownMenuItem>
	);
};

export default EnterpriseCheckStyleGuide;
